import React, { useCallback } from 'react';
import { Trans } from '@lingui/macro';
import ModalContent from 'components/base/modal/modalcontent';
import { Field, Form, Formik, FormikErrors, FormikProps } from 'formik';
import { getExpectedFields } from 'util/backendapi/error';
import { showErrorsInFormik } from 'util/backendapi/error-formik';
import { ReportsAlarmParameter } from 'util/backendapi/types/Model';
import { FormItem, FormSection } from 'components/base/form/FormItem';
import { TransEnum } from 'components/base/i18n/TransEnum';
import { FieldError } from 'components/base/form/errornotice/errornotice';
import ActionBlock from 'components/base/actionblock/actionblock';
import Button, { ButtonPrimary } from 'components/base/button/button';

interface EnableAlarmParameterModalProps {
  alarmParameter: ReportsAlarmParameter;
  onSubmit: (comment: string) => Promise<void>;
  onCancel: () => void;
}

interface EnableAlarmParameterValues {
  comment: string;
}

export function EnableAlarmParameterModal(
  props: EnableAlarmParameterModalProps
) {
  const { onCancel, alarmParameter } = props;

  const validate = useCallback(
    (
      values: EnableAlarmParameterValues
    ): FormikErrors<EnableAlarmParameterValues> => {
      const errors: FormikErrors<EnableAlarmParameterValues> = {};

      if (!values.comment) {
        errors.comment = (<Trans>Please enter a comment.</Trans>) as any;
      }

      return errors;
    },
    []
  );

  return (
    <ModalContent header={<Trans>Enable alarm parameter</Trans>}>
      <Formik<EnableAlarmParameterValues>
        initialValues={{ comment: '' }}
        validate={validate}
        onSubmit={async (values, formik) => {
          try {
            await props.onSubmit(values.comment);
          } catch (e) {
            formik.setSubmitting(false);
            showErrorsInFormik(formik, e, getExpectedFields(values));
          }
        }}
      >
        {(formik: FormikProps<EnableAlarmParameterValues>) => (
          <Form>
            {formik.status}
            <FormSection>
              <Trans>
                Are you sure you want to enable the{' '}
                <strong>
                  <TransEnum
                    enum="AlarmParameter_COMPARISON_TYPE"
                    value={alarmParameter.comparison_type}
                  />{' '}
                  <TransEnum
                    enum="AlarmParameter_LEVEL"
                    value={alarmParameter.level}
                  />{' '}
                  <TransEnum
                    enum="AlarmParameter_TYPE"
                    value={alarmParameter.type}
                  />{' '}
                </strong>{' '}
                alarm parameter?
                <p>
                  Alarm checking will resume and alarm reports will be created
                  if readings exceed this alarm parameter.
                </p>
                <p>
                  This will override any pre-set date to re-enable the alarm
                  parameter.
                </p>
              </Trans>
              <FormItem label={<Trans>Comment</Trans>}>
                <Field component="textarea" name="comment" />
                <FieldError name="comment" />
              </FormItem>
            </FormSection>
            <ActionBlock>
              <Button onClick={onCancel}>
                <Trans>Cancel</Trans>
              </Button>
              <ButtonPrimary type="submit" disabled={formik.isSubmitting}>
                <Trans>Enable</Trans>
              </ButtonPrimary>
            </ActionBlock>
          </Form>
        )}
      </Formik>
    </ModalContent>
  );
}
